import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";

function getScrollDirection(setIsScrolled: (isScrolled: boolean) => void) {
  let ticking = false;

  const updateScrollState = () => {
    const currentScrollY = window.scrollY;

    if (currentScrollY > 0) {
      setIsScrolled(true);
    } else if (currentScrollY === 0) {
      setIsScrolled(false);
    }

    ticking = false;
  };

  const onScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(updateScrollState);
      ticking = true;
    }
  };

  window.addEventListener("scroll", onScroll);
  return () => {
    window.removeEventListener("scroll", onScroll);
  };
}

interface StickyHeaderConfig {
  /** Base sticky position in rem (desktop) */
  baseOffset?: number;
  /** Base sticky position in rem (mobile) - if not provided, uses baseOffset */
  mobileBaseOffset?: number;
  /** Scroll adjustment in rem */
  scrollAdjustment?: number;
  /** Custom background color */
  backgroundColor?: string;
}

const defaultConfig = {
  baseOffset: 4.75,
  mobileBaseOffset: undefined as number | undefined,
  scrollAdjustment: 0.5,
  backgroundColor: "bg-sys-brand-surface"
};

export function withStickyHeader<P extends object>(
  Component: React.ComponentType<P & { isSticky?: boolean }>,
  config: StickyHeaderConfig = {}
) {
  const mergedConfig = { ...defaultConfig, ...config };

  return function StickyHeaderWrapper(props: P) {
    const [isScrolled, setIsScrolled] = useState(false);
    const { isCompactLayout } = useIsCompactLayout();

    const wrapperRef = useRef<HTMLDivElement>(null);
    const nonStickyHeightRef = useRef<number>(0);

    useEffect(() => {
      const cleanup = getScrollDirection(setIsScrolled);
      return cleanup;
    }, []);

    useEffect(
      () => () => {
        document.body.style.paddingBottom = "";
      },
      []
    );

    // When the header goes sticky on mobile, hidden elements shrink it. On
    // short pages this can make the document shorter than the viewport.
    // Compensate by adding padding-bottom to the bodyWe set padding BEFORE reading offsetHeight so the forced
    // reflow never sees the page in its shortened state.
    useLayoutEffect(() => {
      if (!wrapperRef.current) return;

      if (!isScrolled) {
        const height = wrapperRef.current.offsetHeight;
        if (height > 0) nonStickyHeightRef.current = height;
        document.body.style.paddingBottom = "";
      } else if (isCompactLayout && nonStickyHeightRef.current > 0) {
        document.body.style.paddingBottom = `${nonStickyHeightRef.current}px`;
        const stickyHeight = wrapperRef.current.offsetHeight;
        const diff = nonStickyHeightRef.current - stickyHeight;
        document.body.style.paddingBottom = diff > 0 ? `${diff}px` : "";
      } else {
        document.body.style.paddingBottom = "";
      }
    }, [isScrolled, isCompactLayout]);

    // Calculate top offset based on configuration
    const baseOffsetToUse =
      isCompactLayout && mergedConfig.mobileBaseOffset !== undefined
        ? mergedConfig.mobileBaseOffset
        : mergedConfig.baseOffset;
    // Mobile keeps scroll adjustment, desktop uses 0 to prevent jumping
    const scrollAdjustmentToUse = isCompactLayout ? mergedConfig.scrollAdjustment : 0;
    const topOffset = `${baseOffsetToUse - (isScrolled ? scrollAdjustmentToUse : 0)}rem`;

    return (
      <div
        ref={wrapperRef}
        className={`sticky z-30 ${mergedConfig.backgroundColor}`}
        style={{
          top: topOffset,
          // Full width with proper margins
          marginLeft: "-1rem",
          marginRight: "-1rem",
          paddingLeft: "1rem",
          paddingRight: "1rem",
          paddingBottom: isScrolled && isCompactLayout ? "0.375rem" : "0",
          // Subtle bottom shadow only - using inset shadow
          boxShadow: isScrolled ? "inset 0 -1px 0 0 rgb(0 0 0 / 0.05)" : "none",
          transition: "box-shadow 100ms ease-out, padding-bottom 100ms ease-out, top 100ms ease-out"
        }}
      >
        <Component {...props} isSticky={isScrolled} />
        {/* Spacer element for desktop sticky state */}
        {isScrolled && !isCompactLayout && (
          <div
            style={{
              height: "1.5rem",
              transition: "height 100ms ease-out"
            }}
          />
        )}
      </div>
    );
  };
}
